import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Clock, Filter, FileText, Building, Users, RefreshCw, Eye, ExternalLink } from 'lucide-react';
import clsx from 'clsx';
import Button from '@components/common/Button';
import Badge from '@components/common/Badge';
import SearchInput from '@components/common/SearchInput';
import { StatusIcon, Select } from '@components/common';
import { useAuth } from '@contexts/AuthContext';
import { evidenceService } from '@services';
import { ROLES } from '@app-types';
import { hasRole } from '@utils/permissionUtils';
import EvidenceReviewModal from './EvidenceReviewModal';
import { EvidenceForReview } from '@app-types';
import { formatAssessmentId } from '@utils/exportUtils';
import { priorityToVariant, evidenceStatusToVariant } from '@utils/badgeMappers';
import { formatDateOnly } from '@utils/dateUtils';
import sharedStyles from './EvidenceDashboardShared.module.css';
import styles from './EvidenceReviewDashboard.module.css';

const EvidenceReviewDashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Get filters from URL
  const searchTerm = searchParams.get('search') || '';
  const statusFilter = searchParams.get('status') || 'all';
  const priorityFilter = searchParams.get('priority') || 'all';
  const assessmentFilter = searchParams.get('assessment') || 'all';

  // Update filter helper
  const updateFilter = (key: string, value: string) => {
    const newParams = new URLSearchParams(searchParams);
    if (value && value !== 'all' && value !== '') {
      newParams.set(key, value);
    } else {
      newParams.delete(key);
    }
    setSearchParams(newParams);
  };

  // Modal state
  const [selectedEvidence, setSelectedEvidence] = useState<EvidenceForReview | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Fetch pending evidence for this assessor
  const {
    data: evidenceData,
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: ['assessorPendingEvidence'],
    queryFn: () => evidenceService.getPendingEvidenceForAssessor(),
    enabled: !!user && hasRole(user, ROLES.ASSESSOR),
    staleTime: 60 * 1000, // 1-minute cache — avoids re-fetching on every mount (Bug 759)
    refetchInterval: 60 * 1000, // Poll once per minute instead of every 30 s
    refetchOnMount: true,
    refetchOnWindowFocus: false
  });

  // Process and filter data (backend returns latest evidence per assessment_item_id)
  const { filteredItems, stats, uniqueAssessments } = useMemo(() => {
    if (!evidenceData) {
      return {
        filteredItems: [],
        stats: { pending: 0, approved: 0, rejected: 0, total: 0 },
        uniqueAssessments: []
      };
    }

    // Draft Evidence is not in review until the CM submits it; the backend excludes it too.
    const allItems = (evidenceData.evidence || []).filter((item: EvidenceForReview) => item.status !== 'draft');
    // Use statistics from API response
    const apiStats = evidenceData.statistics || { pending: 0, approved: 0, rejected: 0, total: 0 };

    // Filter items based on current filters
    let filtered = allItems.filter((item: EvidenceForReview) => {
      // Search filter
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        const matchesSearch =
          item.process_name?.toLowerCase().includes(searchLower) ||
          item.contract_number?.toLowerCase().includes(searchLower) ||
          item.supplier_name?.toLowerCase().includes(searchLower) ||
          item.assessment_name?.toLowerCase().includes(searchLower) ||
          (item.assessment_id && formatAssessmentId(item.assessment_id).toLowerCase().includes(searchLower));
        if (!matchesSearch) return false;
      }

      // Status filter
      if (statusFilter !== 'all' && item.status !== statusFilter) return false;

      // Priority filter
      if (priorityFilter !== 'all' && item.process_priority !== priorityFilter) return false;

      // Assessment filter
      if (assessmentFilter !== 'all' && item.assessment_name !== assessmentFilter) return false;

      return true;
    });

    // Get unique assessments for filter dropdown
    const assessments = Array.from(
      new Map(
        allItems.map((item: EvidenceForReview) => [
          item.assessment_name,
          { id: item.assessment_name, name: item.assessment_name, contract: item.contract_number }
        ])
      ).values()
    );

    return {
      filteredItems: filtered,
      stats: apiStats,
      uniqueAssessments: assessments
    };
  }, [evidenceData, searchTerm, statusFilter, priorityFilter, assessmentFilter]);

  const handleReviewEvidence = async (evidence: EvidenceForReview) => {
    setSelectedEvidence(evidence);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setSelectedEvidence(null);
    setIsModalOpen(false);
  };

  // Handle metric card click - reset search, priority and assessment filters
  const handleMetricCardClick = (status: string) => {
    const newParams = new URLSearchParams();
    // Only set status, clear everything else (search, priority, assessment)
    if (status && status !== 'all') {
      newParams.set('status', status);
    }
    setSearchParams(newParams);
  };

  if (!user || !hasRole(user, ROLES.ASSESSOR)) {
    return (
      <div className={sharedStyles.dashboardContainer}>
        <div className={sharedStyles.dashboardError}>
          <h2>Access Denied</h2>
          <p>You don't have permission to access the Evidence Review Dashboard.</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={sharedStyles.dashboardContainer}>
        <div className={sharedStyles.dashboardHeader}>
          <div className={sharedStyles.welcomeSection}>
            <h1>Evidence Review</h1>
            <p>Unable to load evidence data</p>
          </div>
        </div>
        <div className={sharedStyles.dashboardError}>
          <h2>Dashboard Unavailable</h2>
          <p>Unable to load evidence data. Please try refreshing the page.</p>
          <Button onClick={() => refetch()}>
            <RefreshCw size={16} />
            Refresh Page
          </Button>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className={sharedStyles.dashboardContainer}>
        <div className={sharedStyles.dashboardHeader}>
          <div className={sharedStyles.welcomeSection}>
            <h1>Evidence Review</h1>
            <p>Loading evidence submissions...</p>
          </div>
        </div>
        <div className={sharedStyles.loadingState}>
          <RefreshCw className="animate-spin" size={32} />
          <p>Loading evidence items...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={sharedStyles.dashboardContainer}>
      {/* Welcome Header */}
      <div className={sharedStyles.dashboardHeader}>
        <div className={sharedStyles.welcomeSection}>
          <h1>Evidence Review</h1>
          <p>Review evidence submissions from assigned Contract Role users across your assigned assessments.</p>
        </div>
        <div className={sharedStyles.dashboardMeta}>
          <span className={sharedStyles.userRole}>Assessor</span>
          <span className={sharedStyles.lastUpdated}>Last updated: {new Date().toLocaleTimeString()}</span>
        </div>
      </div>

      {/* Evidence Completion Progress */}
      <div className={styles.evidenceProgressSection}>
        <h3>Review Progress</h3>
        <div className={styles.progressStats}>
          <button
            type="button"
            className={clsx(
              styles.progressStat,
              styles.progressStatPending,
              'cursor-pointer',
              statusFilter === 'pending' && 'active'
            )}
            onClick={() => handleMetricCardClick('pending')}
            data-test-id="evidence-review-stat-pending"
          >
            <span className={styles.statValue}>{stats.pending}</span>
            <span className={styles.statLabel}>Pending Review</span>
          </button>
          <button
            type="button"
            className={clsx(
              styles.progressStat,
              styles.progressStatApproved,
              'cursor-pointer',
              statusFilter === 'approved' && 'active'
            )}
            onClick={() => handleMetricCardClick('approved')}
            data-test-id="evidence-review-stat-approved"
          >
            <span className={styles.statValue}>{stats.approved}</span>
            <span className={styles.statLabel}>Approved</span>
          </button>
          <button
            type="button"
            className={clsx(
              styles.progressStat,
              styles.progressStatRejected,
              'cursor-pointer',
              statusFilter === 'rejected' && 'active'
            )}
            onClick={() => handleMetricCardClick('rejected')}
            data-test-id="evidence-review-stat-rejected"
          >
            <span className={styles.statValue}>{stats.rejected}</span>
            <span className={styles.statLabel}>More Info Needed</span>
          </button>
          <button
            type="button"
            className={clsx(
              styles.progressStat,
              styles.progressStatTotal,
              'cursor-pointer',
              statusFilter === 'all' && 'active'
            )}
            onClick={() => handleMetricCardClick('all')}
            data-test-id="evidence-review-stat-total"
          >
            <span className={styles.statValue}>{stats.total}</span>
            <span className={styles.statLabel}>Total Evidence</span>
          </button>
        </div>
        <div className={styles.progressBarContainer}>
          <div className={styles.progressBar}>
            <div
              className={clsx(styles.progressBarFill, styles.progressBarFillApproved)}
              style={{ width: `${stats.total > 0 ? (stats.approved / stats.total) * 100 : 0}%` }}
            />
            <div
              className={clsx(styles.progressBarFill, styles.progressBarFillRejected)}
              style={{ width: `${stats.total > 0 ? (stats.rejected / stats.total) * 100 : 0}%` }}
            />
          </div>
          <div className={styles.progressText}>
            {stats.total > 0
              ? `${Math.round(((stats.approved + stats.rejected) / stats.total) * 100)}% Reviewed`
              : 'No evidence to review'}
          </div>
        </div>
      </div>

      {/* Main Dashboard Grid */}
      <div className={sharedStyles.dashboardGrid}>
        {/* Filters and Evidence List Row */}
        <div className={sharedStyles.dashboardRow}>
          <div className={sharedStyles.dashboardCard}>
            <div className={sharedStyles.cardHeader}>
              <h3>
                <Filter size={18} />
                Evidence Review Queue
              </h3>
              <div className={sharedStyles.headerActions}>
                <Button variant="ghost" onClick={() => refetch()}>
                  <RefreshCw size={16} />
                  Refresh
                </Button>
              </div>
            </div>

            <div className={sharedStyles.cardContent}>
              {/* Filters */}
              <div className={sharedStyles.filtersGrid}>
                <div className={sharedStyles.filterGroup}>
                  <label htmlFor="evidence-review-search">Search</label>
                  <SearchInput
                    id="evidence-review-search"
                    value={searchTerm}
                    onChange={(value) => updateFilter('search', value)}
                    placeholder="Search evidence, assessments, contracts..."
                  />
                </div>

                <div className={sharedStyles.filterGroup}>
                  <Select
                    label="Status"
                    value={statusFilter}
                    onChange={(value) => updateFilter('status', value)}
                    options={[
                      { value: 'pending', label: 'Pending Review' },
                      { value: 'approved', label: 'Approved' },
                      { value: 'rejected', label: 'More Info Needed' },
                      { value: 'all', label: 'All Statuses' }
                    ]}
                    className={sharedStyles.filterSelect}
                    dataTestId="evidence-review-filter-status"
                  />
                </div>

                <div className={sharedStyles.filterGroup}>
                  <Select
                    label="Priority"
                    value={priorityFilter}
                    onChange={(value) => updateFilter('priority', value)}
                    options={[
                      { value: 'all', label: 'All Priorities' },
                      { value: 'High', label: 'High Priority' },
                      { value: 'Medium', label: 'Medium Priority' },
                      { value: 'Low', label: 'Low Priority' }
                    ]}
                    className={sharedStyles.filterSelect}
                    dataTestId="evidence-review-filter-priority"
                  />
                </div>

                <div className={sharedStyles.filterGroup}>
                  <Select
                    label="Assessment"
                    value={assessmentFilter}
                    onChange={(value) => updateFilter('assessment', value)}
                    options={[
                      { value: 'all', label: 'All Assessments' },
                      ...uniqueAssessments.map((assessment: { id: string; name: string; contract: string }) => ({
                        value: assessment.id,
                        label: `${assessment.name} (${assessment.contract})`
                      }))
                    ]}
                    className={sharedStyles.filterSelect}
                    dataTestId="evidence-review-filter-assessment"
                  />
                </div>
              </div>

              {/* Evidence List */}
              <div className={sharedStyles.evidenceListSection}>
                <div className={sharedStyles.listHeader}>
                  <span className={sharedStyles.itemsCount}>
                    {filteredItems.length} of {stats.total} evidence submissions
                  </span>
                </div>

                {filteredItems.length === 0 ? (
                  <div className={sharedStyles.emptyState} data-test-id="evidence-review-empty-state">
                    {stats.pending === 0 ? (
                      <>
                        <StatusIcon status="success" size={48} />
                        <h4>All Caught Up!</h4>
                        <p>No evidence submissions require your review at this time.</p>
                      </>
                    ) : (
                      <>
                        <StatusIcon status="warning" size={48} />
                        <h4>No Items Match Filters</h4>
                        <p>Try adjusting your filters to see more results.</p>
                      </>
                    )}
                  </div>
                ) : (
                  <div className={sharedStyles.gapItemsList}>
                    {filteredItems.map((evidence: EvidenceForReview) => (
                      <div key={evidence.id} className={sharedStyles.gapItem} data-test-id="evidence-review-item">
                        <div className={sharedStyles.itemHeader}>
                          <div className={sharedStyles.itemInfo}>
                            <div className={sharedStyles.itemTitle}>
                              <strong>{evidence.evidence_name}</strong>
                              <Badge variant={evidenceStatusToVariant(evidence.status)}>
                                {evidence.status === 'pending' && 'Pending Review'}
                                {evidence.status === 'approved' && 'Approved'}
                                {evidence.status === 'rejected' && 'More Info Needed'}
                              </Badge>
                              <span className={styles.evidenceCounts}>
                                ({evidence.files?.length || 0} file{evidence.files?.length !== 1 ? 's' : ''})
                              </span>
                            </div>
                            <div className={sharedStyles.gapContext}>
                              <strong>Process:</strong> {evidence.process_name}
                              {evidence.process_priority && (
                                <Badge variant={priorityToVariant(evidence.process_priority)}>
                                  {evidence.process_priority} Priority
                                </Badge>
                              )}
                            </div>
                            {evidence.review_notes && evidence.status === 'rejected' && (
                              <div className={styles.assessorFeedback}>
                                <strong>Previous Feedback:</strong> {evidence.review_notes}
                              </div>
                            )}
                            <div className={sharedStyles.itemMeta}>
                              <div className={sharedStyles.metaItem}>
                                <FileText size={14} />
                                <span>{evidence.contract_number}</span>
                              </div>
                              <div className={sharedStyles.metaItem}>
                                <Building size={14} />
                                <span>{evidence.supplier_name}</span>
                              </div>
                              <div className={sharedStyles.metaItem}>
                                <Users size={14} />
                                <button
                                  className={sharedStyles.assessmentLink}
                                  onClick={() => navigate(`/assessments/${evidence.assessment_id}`)}
                                  title="View assessment details"
                                >
                                  {formatAssessmentId(evidence.assessment_id)}: {evidence.assessment_name}
                                  <ExternalLink size={12} />
                                </button>
                              </div>
                              <div className={sharedStyles.metaItem}>
                                <Clock size={14} />
                                <span>Submitted {formatDateOnly(evidence.submitted_at ?? evidence.created_at)}</span>
                              </div>
                            </div>
                          </div>
                          <div className={sharedStyles.itemActions}>
                            <Button
                              variant={evidence.status === 'pending' ? 'primary' : 'ghost'}
                              size="sm"
                              onClick={() => handleReviewEvidence(evidence)}
                            >
                              <Eye size={14} />
                              {evidence.status === 'pending' ? 'Review Evidence' : 'View Details'}
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Evidence Review Modal */}
      {selectedEvidence && (
        <EvidenceReviewModal evidence={selectedEvidence} isOpen={isModalOpen} onClose={handleCloseModal} />
      )}
    </div>
  );
};

export default EvidenceReviewDashboard;
